import { useState } from 'react';
import { allRulesSatisfied, evaluatePasswordRules } from '@wineryxplorer/shared';
import { Button } from '../ui/Button';
import { PasswordRequirements } from '../auth/PasswordRequirements';
import { trpc } from '../../lib/trpc';
import { FormSection } from '../ui/FormSection';

interface ChangePasswordSectionProps {
  hasPassword: boolean;
}

export const ChangePasswordSection = ({ hasPassword }: ChangePasswordSectionProps) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const changePassword = trpc.auth.changePassword.useMutation({
    onSuccess: () => {
      setSuccessMessage('Password changed successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
  });

  if (!hasPassword) {
    return (
      <FormSection title="Change Password">
        <p className="text-sm text-gray-500">
          Your account uses OAuth sign-in. To set a password, use the "Forgot password" flow from the
          login page.
        </p>
      </FormSection>
    );
  }

  const passwordRules = evaluatePasswordRules(newPassword);
  const passwordMeetsRequirements = allRulesSatisfied(passwordRules);
  const passwordsMatch = newPassword === confirmPassword;
  const confirmTouched = confirmPassword.length > 0;
  const newPasswordTouched = newPassword.length > 0;
  const canSubmit =
    oldPassword.length > 0 &&
    passwordMeetsRequirements &&
    passwordsMatch &&
    !changePassword.isPending;

  const handleChangePassword = () => {
    setSuccessMessage(null);
    changePassword.mutate({ oldPassword, newPassword });
  };

  return (
    <FormSection
      title="Change Password"
      contentClassName="space-y-4 max-w-md"
      actions={
        <>
          {changePassword.error && (
            <p className="text-sm text-red-600">{changePassword.error.message}</p>
          )}
          {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}
          <Button
            variant="primary"
            size="sm"
            onClick={handleChangePassword}
            disabled={!canSubmit}
            loading={changePassword.isPending}
          >
            Update Password
          </Button>
        </>
      }
    >
        <div>
          <label htmlFor="old-password" className="block text-sm font-medium text-gray-700 mb-1">
            Current Password
          </label>
          <input
            id="old-password"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            disabled={changePassword.isPending}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50"
          />
        </div>
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
            New Password
          </label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={changePassword.isPending}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50 ${
              newPasswordTouched && !passwordMeetsRequirements
                ? 'border-red-400'
                : 'border-gray-300'
            }`}
          />
          <div className="mt-2">
            <PasswordRequirements password={newPassword} rules={passwordRules} />
          </div>
        </div>
        <div>
          <label
            htmlFor="confirm-password"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Confirm New Password
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={changePassword.isPending}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50 ${
              confirmTouched && !passwordsMatch ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {confirmTouched && !passwordsMatch && (
            <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
          )}
        </div>
    </FormSection>
  );
};
